import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'api/v1/cms/exclusive-deals';

export type DealHistorySource =
  | 'cms_update'
  | 'sheet_upload'
  | 'cms_bulk_upsert'
  | 'price_list_import'
  // A CMS inquiry quote saved with "Save to Price tool".
  | 'inquiry_quote'
  // An inquiry quote being awarded — the price becomes the standing one.
  | 'inquiry_award';

/** The inquiry an inquiry_quote / inquiry_award entry came from. */
export interface DealHistoryInquiryRefDto {
  inquiryId: number;
  quoteId: number;
}

export interface DealHistoryAuthorDto {
  id: number;
  name: string;
  email: string | null;
}

/** Full post-update copy of the deal fields stored with each history entry. */
export interface DealHistorySnapshotDto {
  productId: number;
  dealPrice: number;
  // Ladder rung the price was agreed against; absent on snapshots written
  // before 2026-08-12 (unrecorded, distinct from an explicit null).
  dealUnit?: 'PACK' | 'ITEM' | 'BASE' | null;
  rebatePercent: number | null;
  restaurantSharePercent: number | null;
  cashbackPercent: number | null;
  countryCode: string;
  isActive: boolean;
  startDate: string | null;
  endDate: string | null;
  productExpiryDate: string | null;
  isNearExpiry: boolean;
  // Present on entries written from the inquiry desk (quote push or award).
  inquiry?: DealHistoryInquiryRefDto;
}

export interface DealHistoryEntryDto {
  id: number;
  dealId: number;
  source: DealHistorySource;
  snapshot: DealHistorySnapshotDto;
  author: DealHistoryAuthorDto | null;
  createdAt: string;
}

interface ResponseData {
  data: DealHistoryEntryDto[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [string, { dealId: number; page: number; limit: number }];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
  enabled?: boolean;
};
type ReturnType = UseQueryResult<TData, TError>;

/**
 * Fetch paginated update history for a specific deal (newest first).
 */
export function useGetDealHistoryApi(dealId: number | null, page = 1, limit = 10, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();
  const { enabled: userEnabled = true, ...restOptions } = options || {};

  const queryKey: TQueryKey = [`${endpoint}/${dealId}/history`, { dealId: dealId || 0, page, limit }];

  const queryFn = async (): Promise<TData> => {
    try {
      const { data } = await http.get(`${endpoint}/${dealId}/history`, {
        params: { page, limit },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(queryKey, queryFn, {
    enabled: isLoggedIn && userEnabled && !!dealId,
    ...restOptions,
  });
}
